export interface BlogPost {
  id: string;
  title: string;
  date: string;
  tags: string[];
  logo?: string;
  content: string;
  excerpt?: string;
}


export const blogPosts: BlogPost[] = [
  {
    id: 'dynamic-code-execution',
    title: 'Dynamic Code Execution',
    date: 'September 20, 2019',
    tags: ['Apex', 'Assets', 'Blog Posts', 'Salesforce'],
    logo: '/images/blogposts/dynamic_code_execution/logo.png',
    excerpt: 'A configurable solution to dynamically enable or disable Apex triggers, classes, and methods using Custom Metadata Types.',
    content: `## Background

Everyone loves configurations and has control over things through configurable options. In the Salesforce ecosystem, it's really hard to tweak things sitting under a codebase in Production as it needs multiple steps to do the changes, deploy the changes and its counter deployment to enable them again.

And it becomes very hard when we need it just for testing, debugging or reproducing the issue in production. Again having apex code coverage compliance make it a more difficult and time-taking activity.

In my old days, I have seen people using a custom setting to have a checkbox for every object for which we want the trigger to be turned on or off.

Well that was a really good asset and I have used it many times but the problem with that was that it was not scalable and dynamic, for each new object I want to control I had to create a new checkbox field and it was only for the triggers, what about the aura-enabled methods, invocable apex or any rest service or any commonly used utility method ??

If you also feel the same way, you are in right place, as now I have a solution for it, I took the inspiration from the same custom setting and enhanced it to solve all our problems

## Problem Statement

- In our project, there is another team that is doing the Data Migration and that activity is having a dependency on the codebase (especially on the object model), so it should be performed after the code deployment. But as we know many times we don't want the automation to happen so we usually disabled the triggers but that's not possible in PROD very easily, we need to deploy changesets for the Inactive triggers and then Active triggers after DM activities are done
- The other problem is that we want the ability to easily turn on or off the execution of any Trigger/Class/Method

## Solution

- We have seen many projects using Disable Trigger custom setting which is having a checkbox for a single object that needs to be used in the Trigger
    - But the issue with that approach is we need to create a new custom field on the custom setting and then only we can use that in the trigger
    - And that is solving only one purpose of disabling the triggers, not work for the apex class and any individual method
- So we came us with an enhanced version of that custom setting as a Custom Metadata type which is easily adaptable and expendable
    - The Custom MetaData Type is having below fields on it
        - Apex_Class_Trigger_Name__c – It should be having an apex trigger or apex class name
        - Method_Name__c – (Optional) It should be having the method name. Don't provide it when want to disable the complete Apex Trigger or Class mentioned in the above field. I want to disable a specific method from provided apex class and then only provide that method's name in it
        - Disable_Execution__c – We need to check this checkbox if we want to disable the execution of
        - Description__c – (Optional) Description for this record

## Sample Records

![Sample Records](/images/blogposts/dynamic_code_execution/sample_records.png)

## Examples

![Example 1](/images/blogposts/dynamic_code_execution/example1.png)

![Example 2](/images/blogposts/dynamic_code_execution/example2.png)

## Resources

- **Apex Classes**
    - DynamicCodeExecution – core apex utility class
    - DynamicCodeExecution_Test – Test class for the utility class
- **Custom Metadata Type**
    - Dynamic_Code_Execution__mdt – The Custom Metadata Type object
- **Custom Fields**
    - Apex_Class_Trigger_Name__c – Custom field on Metadata type
    - Method_Name__c – Custom field on Metadata type
    - Disable_Execution__c – Custom field on Metadata type
    - Description__c – Custom field on Metadata type
- **Metadata Record**
    - DO_NOT_DELETE – A metadata record that is needed for test class code coverage
- **List View**
    - All – Metadata list view

## Pre-Deployment Steps

This is a simple plug and plays apex utility but still, please make sure to follow the below bullet points

- Before deploying the package, Please take a backup of your target org and make sure package resources should not be available in the target org.
- If package resources are available in it then please do not deploy and try to merge existing package resources.

## Deployment Steps

You can access the codebase here on GitHub and deploy it to your playground/sandbox/developer org

**Source:** https://github.com/basantverma1990/sfdc_assets/tree/apex/dynamic-code-execution

## Post-Deployment Steps

- Deploy the resources in the target org.
- Start using the utility method in all apex triggers, classes and their methods
    - Utility Class: DynamicCodeExecution
    - Method to use: allowExecution
        - Input Parameter: String – Should be the Apex Trigger name on when used for apex trigger. And it should have <apex class name>.<method name> when used for any method
            - Example1 – For Trigger
                - DynamicCodeExecution.allowExecution('AccountTrigger')
            - Example2 – For method
                - DynamicCodeExecution.allowExecution('AccountTriggerHandler.updateContactOwner')
        - Returns: Boolean – It returns TRUE if execution is allowed, and FALSE if execution is NOT allowed`
  },
  {
    id: 'quick-links',
    title: 'Quick Links',
    date: 'September 10, 2019',
    tags: ['Assets', 'Aura Component', 'Blog Posts', 'Salesforce'],
    logo: '/images/blogposts/quick_links/logo.png',
    excerpt: 'A configurable Lightning Component for creating custom quick links that can be displayed on any Lightning page.',
    content: `## Problem Statement

- Want to see multiple links on the home page or any sObject details page
- Links on all places are not identical
- Links can be grouped into categories
- Categories have collapsed and expanded capabilities
- Links can be static and dynamic
- Categories can be configured to be expanded by default or not
- Admins can configure the link details and URLs
- Admins can also add or remove links easily


## Solution

- We have built a custom object called Custom_Link__c
- To handle the grouping we created two record types on the Custom Links
    - Category – This is the Grouping category and will be having details related to a category such as
        - Label__c – Stores the Category Label
        - Description__c – Any description Admin want to have for more details
        - Order__c – The order of display on the UI
        - Collapsed__c – The Boolean flag used to show the category as by default collapsed or expanded. False means it will be shown as Expanded, true means it will be collapsed
        - IsActive__c – The Boolean flag to make active or inactive, only active links and categories will be visible on UI. This is false on category but child links are active, still, they will not be shown
    - Custom Link – These are the actual links under the category where users can click and navigate, this record type is having fields that are related to links such as
        - Label – Label for the link
        - Category__c – Lookup to the parent category
        - Category_Name__c – This is just a formula field to the parent Category’s label
        - Link__c – The link where we should redirect on click
        - Description__c – Description for the link which will be visible on UI as well
        - Location__c – This is defined where we need to show this link, like the Home page vs the Opportunity Detail page. These are the only two values we are using right now but these can be easily extended just by adding new picklist values
        - Order__c – The order of display on the UI
        - IsActive__c – The Boolean flag to make active or inactive, only active links and categories will be visible on UI
    - Custom Lightning component name QuickLinksComp
        - It is having one configurable property named Location which is nothing but just all picklist values from the custom field Location__c from Custom_Link__c object
        - We need to explicitly select the correct value based on which page we are adding it.
            - If we are adding it to the Home page, we should select Home
            - If we are adding it to the Opportunity detail page, we should select Opportunity
        - This component then looks for all the custom link records of the “Custom Link” record type where an active flag is checked on both Custom Link and its parent Category record
        - This component will build the UI based on the configuration done on the custom link level

## Examples
Category & Custom Links
![Example1](/images/blogposts/quick_links/example1.png)
![Example 2](/images/blogposts/quick_links/example2.png)

- Component Configuration when adding it to the lightening page
- This is how it looks like on the Home and Opportunity pages

![Example 3](/images/blogposts/quick_links/example3.png) 

## Resources

- **Apex Classes**
    - QuickLinksCompCtrl – Controller for the custom lightning component
    - QuickLinksCompCtrl_Test – Test class for the controller class
    - QuickLinksPicklistCtrl – Apex Class for the Lightning Component's parameter at the design attribute level to fetch the picklist values from the Location field of the custom link object
    - QuickLinksPicklistCtrl_Test – Test class for the QuickLinksPicklistCtrl

- **Lightning Component**
    - QuickLinksComp – The lightning component which reads the configuration and builds the UI for us

- **Custom Object**
    - Custom_Link__c – The Custom object to configure both category and custom links

- **Record Types**
    - Category – Record type for the Category type custom links which is the high-level grouping
    - Custom_Link – The record type for the actual custom link shown under the categories

- **List View**
    - All_Categories – List view which shows all the Categories
    - All_Custom_Links – List view which shows all the Custom Links

## Pre-Deployment Steps

Though this is a simple plug-and-play utility please make sure to follow the below points

- Before deploying the package, Please take a backup of your target org and make sure package resources should not be available in the target org.
- If package resources are available in it then please do not deploy and try to merge existing package resources.

- This package’s code is using two other assets so please make sure you have those installed as well, if don’t want to use them, please manually remove their references from the code. Those are
    - Base Component
    - Dynamic Code Execution

## Deployment Steps

You can access the codebase here on GitHub and deploy it to your playground/sandbox/developer org

**Source:** https://github.com/basantverma1990/sfdc_assets/tree/apex/quick-links

## Post-Deployment Steps

- Deploy the resources in the target org.
- Please give them permission and FLS of the deployed objects and their fields.
- Grant access to the lightning component's controller class to respective profiles
- Configure the custom links category and custom link records
- Start using as per your need.`
  },
  {
    id: 'inheritance-in-aura',
    title: 'Inheritance in Aura',
    date: 'September 3, 2017',
    tags: ['Aura Component', 'Blog Posts', 'Salesforce'],
    excerpt: 'Learn about implementing inheritance patterns in Aura Components for better code reusability.',
    logo: '/images/blogposts/inheritance_in_aura/logo.png',
    content: `## Background
Inheritance is such a cool feature in high-level programming languages, and yes it’s available in AuraComponents too, we can create an aura component with an extensible=”true” tag and it will do its magic so that the component is available to be inherited by other components.

The key to inheritance is to write a bunch of reusable/common/generic logic once and reuse it from all child components.

Since I was introduced to this feature of AuraComponent I started using BaseComponent in almost every project I worked on aura components, since then I have kept enhancing and increasing the capabilities of the base component and now it became an asset for me. happy to share it with you all too.

## Problem Statement

- While building lightning components there are so many things that are needed for each component and are very common for any component, such as calling aura-enabled apex method, showing/hiding loading spinner, error handling, navigation and many more
- Many times a single typo mistake anywhere in the controller or helper javascript takes so much time to debug and fix because lake of error reporting and compilation of the aura components
- The other main challenge is when we want to use the Lightning Component from classic UI, we normally use the lightning out approach for this, but many events are not working in that scenario
- Want to apply some branding or some special handling for all custom components

## Solution

- Since Lightning Components are also supporting inheritance, so we came up with a generic base component that can hold all basic and common functions that are used in multiple places and extend this base component to all our custom components
- This is having conditional handling for the Classic vs Lightning experience for various things such as events, toast messages, navigations and many more
- Below is the list of commonly used methods on the helper level
    - startWaiting – Start showing the spinner, usually we use it just before starting any action
    - stopWaiting – Stop showing the spinner, the counter method of the startWaiting, usually we use it just after all execution is completed.
    - doCallout – This is the method to invoke the call to AuraEnabled apex method, we can pass, the method name, all parameters and call back method as well
    - navigateToRecord – This is a navigation method that navigates to the provided record Id’s detail page, its having handling for both lightning and classic experience
    - handleErrors – This is a very useful method that can be used in doCallout’s call back a method in case of error, this method is handling all kind of errors and show toast message for errors
    - getAllUrlParams – This method is used to fetch all URL parameters from the provided URL
    - doShowToast – This is the method to explicitly show the toast message, this is also having to handle for both Classic and Lightning Experience
    - doCloseModel – To close the open model window, using the overlay library event in this method
    - myLog – This is also a small but very helpful method, we should avoid using console.log from anywhere in custom components and use this instead which will help us for production deployment that if we want we can add any prefix/suffix to the console log and can also completely disable the console log just by modifying this one method.
    - doRefresh – Method to refresh the view, in LEX it will use the force:refrehView and in classic, it will reload the page
    - doNewModel – Utility method to open a new model window for provided component and its attributes
    - doNavigateToObjectHome – This is also a navigation method that navigates to the sObject home page (Tab) view. Only work in LEX, the classic will show an error toast that this kind of navigation is possible
    - doShowNotif – The method is using notification library and shows the notification, for classic will show the toast
    - doNavigateToCommunityHome – Used for communities only to navigate to the home page
    - closeAllToasts – Method to close all open toast messages (if they are created with sticky mode, as dismissible will automatically disappear after provided millisecond time) also works in classic
    - createRecord – Method which is using native force:createRecord event
    - closeFocusedTab – Method to close the currently focused tab when in the console
    - consoleOpenTab – Method to open a tab in the console app

## Examples

Extending it in the custom Component – We just need to use the existing extends tag which is available on aura:component level

![Example1](/images/blogposts/inheritance_in_aura/example1.png)

Calling base component’s helper methods from custom components’ controller – For this, we can use the helper reference itself, since we are using inheritance, it will first check if the same name method is available in the local helper, if not, it will make the call to the parent (BaseComponent) helper

![Example 2](/images/blogposts/inheritance_in_aura/example2.png)

Calling from custom component’s helper – For this also we need a helper reference to call it if we are passing a helper reference from the controller we can use the same, if not we can use (this) annotation which is also a reference to helper only

![Example 3](/images/blogposts/inheritance_in_aura/example3.png) 

## Resources

- **Lightning Components**
    - BaseComponent – Common lightning component we are using in all components, can be used individually as well


## Pre-Deployment Steps

Its a simple plug-and-play component but still please make sure for the below bullet points

- Before deploying the package, Please take a backup of your target org and make sure package resources should not be available in the target org.
- If package resources are available in it then please do not deploy and try to merge existing package resources.

- This package’s code is using two other assets so please make sure you have those installed as well, if don’t want to use them, please manually remove their references from the code. Those are
    - Base Component
    - Dynamic Code Execution

## Deployment Steps

You can access the codebase here on GitHub and deploy it to your playground/sandbox/developer org

**Source:** https://github.com/basantverma1990/sfdc_assets/tree/aura/BaseComponent

## Post-Deployment Steps

- Deploy the resources in the target org.
- Start extending this component in your custom component and start using it, refer example section for more details`
  }
];
